import Grid from 'components/UI/atoms/Grid';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface IProps {
  currentTab?: string;

  tabNames: string[];

  tabNodes: ReactNode[];

  size?: 'small' | 'full';

  tabSwitchEvent?: any;

  tabSwitchValues?: string[] | number[];

  /* 탭 활성 색상 (기본값: #0072CE) */
  color?: string;
}

interface ITabTagProps extends Pick<IProps, 'size' | 'color'> {
  swtichState: number;
  index: number;
}

interface ITabWrapProps extends Pick<IProps, 'size'> {}

const TabWrap = styled.div<ITabWrapProps>`
  position: relative;
  border-bottom: ${({ size }) => (size && size === 'small' ? `1px solid transparent` : '1px solid #aaaa')};
  padding-bottom: 4px;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  gap: 20px;
  overflow: visible;
`;

const TabSlider = styled.span<{ $color: string }>`
  position: absolute;
  top: 0;
  height: 3px;
  background: ${({ $color }) => $color};
  border-radius: 2px 2px 0 0;
  transition:
    left 0.25s ease-out,
    width 0.25s ease-out,
    top 0.25s ease-out;
  pointer-events: none;
`;

const TabTag = styled.span<ITabTagProps>`
  font-weight: ${({ swtichState, index }) => (swtichState === index ? `500` : '400')};
  color: ${({ swtichState, index, color = '#0072CE' }) => (swtichState === index ? color : '#aaaa')};
  font-size: ${({ size }) => (size && size === 'small' ? `16px;` : '18px;')};
  padding: ${({ size }) => (size && size === 'small' ? `0px 5px 10px 5px;` : '0px 50px 20px 50px;')};
  margin: ${({ size }) => (size && size === 'small' ? `0px 40px 0px 0px;` : '')};
  text-align: center;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

function Tab({ currentTab, tabNames, tabNodes, size, tabSwitchEvent, tabSwitchValues, color = '#0072CE' }: IProps) {
  const [swtichState, setSwitchState] = useState<number>(0);
  const [sliderStyle, setSliderStyle] = useState<{ left: number; width: number; top: number }>({
    left: 0,
    width: 0,
    top: 0,
  });
  const wrapRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLSpanElement | null)[]>([]);

  const updateSliderPosition = useCallback(() => {
    const wrap = wrapRef.current;
    const activeTab = tabRefs.current[swtichState];
    if (!wrap || !activeTab) return;
    const wrapRect = wrap.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    setSliderStyle({
      left: tabRect.left - wrapRect.left + wrap.scrollLeft,
      width: tabRect.width,
      top: tabRect.bottom - wrapRect.top + 4,
    });
  }, [swtichState]);

  const switchTab = (state: number) => {
    setSwitchState(state);
    tabSwitchValues && tabSwitchEvent && tabSwitchEvent(tabSwitchValues[state]);
  };

  useEffect(() => {
    if (currentTab && tabNames.indexOf(currentTab) !== swtichState) {
      switchTab(tabNames.indexOf(currentTab));
    }
  }, [currentTab]);

  useEffect(() => {
    updateSliderPosition();
    const resizeObserver = new ResizeObserver(updateSliderPosition);
    if (wrapRef.current) resizeObserver.observe(wrapRef.current);
    window.addEventListener('resize', updateSliderPosition);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSliderPosition);
    };
  }, [swtichState, tabNames.length, updateSliderPosition]);

  return (
    <Grid>
      <TabWrap size={size}>
        {tabNames.map((name, index) => {
          return (
            <TabTag
              key={name}
              size={size}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              color={color}
              onClick={() => switchTab(index)}
              swtichState={swtichState}
              index={index}
            >
              {name}
            </TabTag>
          );
        })}
      </TabWrap>
      <Grid>{tabNodes[swtichState]}</Grid>
    </Grid>
  );
}

export default Tab;
